import { memo, Suspense, useMemo, useRef } from 'react'
// import _ from 'lodash'
import { isValidElementType, isLazy, Lazy } from 'react-is'
import { NavLink } from 'react-router-dom'
import classNames from 'classnames'
import _ from 'lodash'
import { isValidRoute, renderTitle } from './ui-lib/utils'
import { FAWKES_MICRO_APP_NAME, MULTI_TENANT_PATH_PREFIX_REGEX } from '../../framework/constants'
import { history, getFrameworkVar } from '../../framework/states'
import NavIcon from '../common/NavIcon'
import './NavActions.scss'
import type { VFC, ElementType, ComponentType, MouseEvent } from 'react'
import type { NavAction, LazyFactory } from '../../framework/microAppTypes'
import type { AnyState, StoreState } from '../../framework/stateTypes'
import NavErrorBoundary from '../common/NavErrorBoundary'

const isLazyType = (Comp: ElementType | LazyFactory) => (
  isLazy(Comp) || (Comp as { $$typeof?: symbol }).$$typeof === Lazy
)
const isPropsArrayEqual = (previous: AnyState[], next: AnyState[]) => { // only compare two levels deep
  if (previous === next) {
    return true
  }
  if (previous.length !== next.length) {
    return false
  }
  return next.every((each, i) => _.isEqual(each, previous[i]))
}

const preventDefault = (e: MouseEvent<HTMLAnchorElement>) => {
  e.stopPropagation()
  e.preventDefault()
}

type IndexedStateArray = AnyState[] & Record<string, AnyState>
type NavActionsProps = { isSideNavCollapsed: boolean, hideNavRoutes: boolean, hasFawkes?: boolean, actions: NavAction[], state: StoreState }
const NavActions: VFC<NavActionsProps> = ({ isSideNavCollapsed, hideNavRoutes, hasFawkes, actions, state }) => {
  const collapsedCls = isSideNavCollapsed ? 'collapsed' : ''
  const prevPropsRef = useRef<IndexedStateArray>()
  const isInMultiTenantView = MULTI_TENANT_PATH_PREFIX_REGEX.test(window.location.pathname) // TODO: temp logic
  const filteredActions = useMemo(() => {
    const alwaysShowCoveo = getFrameworkVar<boolean>('always_show_coveo')
    const filteredActions = hasFawkes && !isInMultiTenantView ? actions : actions.filter(act =>
      act.appName !== FAWKES_MICRO_APP_NAME || // TODO: temp logic
      (alwaysShowCoveo && act.key === 'coveo')
    )
    return filteredActions.filter(act => !act.asPlaceholder).filter(act => !act.avail || isValidRoute({ noRBACheck: true, callAvail: true, ...act }))
  }, [actions, isInMultiTenantView, hasFawkes])
  const stateToProps = useMemo(() => {
    if (!filteredActions?.length) {
      return
    }
    const props = filteredActions.reduce((all, action, i) => {
      const indexed = all as IndexedStateArray
      const props = action.mapStateToProps?.(state)
      if (props) {
        if (action.mspActions) {
          props.mspActions = action.mspActions.map(({ key, navComponent, mapStateToProps }: NavAction) => {
            const NavComponent = navComponent as ComponentType
            const actProps = mapStateToProps?.(state)
            return (
              <NavComponent key={key} {...actProps} />
            )
          })
        }
        indexed.push(props)
        indexed[action.key || `idx${i}`] = props
      }
      return all
    }, [] as AnyState[]) as IndexedStateArray
    if (!props.length) {
      return
    }
    if (prevPropsRef.current && isPropsArrayEqual(prevPropsRef.current, props)) {
      return prevPropsRef.current
    }
    prevPropsRef.current = props
    return props
  }, [filteredActions, state])
  const navItems = useMemo(() => filteredActions?.map((action, i) => {
    let rendered
    const icon = <NavIcon icon={action.icon} className='nav-action-icon' />
    const title = isSideNavCollapsed ? <p className='sparky-left-nav-tooltip'>{renderTitle(action.title)}</p> : renderTitle(action.title)
    const cls = classNames('nav-action-link', collapsedCls, action.className)
    if (action.navComponent && isValidElementType(action.navComponent)) {
      const actionCompProps = { isSideNavCollapsed, ...stateToProps?.[action.key || `idx${i}`] }
      if (isLazyType(action.navComponent)) {
        const Component = action.navComponent as ElementType

        // TODO: use something else
        const fallback = <a href='#' className={classNames('nav-link', cls)} onClick={preventDefault}>
          {icon}
          {title}
        </a>
        rendered = (
          <Suspense fallback={fallback}>
            <Component
              history={history}
              {...actionCompProps}
              className={cls}
            >
              {icon}
              {title}
            </Component>
          </Suspense>
        )
      }
      else {
        const Component = action.navComponent as ElementType
        rendered = (
          <Component
            history={history}
            {...actionCompProps}
            className={cls}
          />
        )
      }
    }
    else if (action.path && action.icon && action.title) {
      if (action.path) {
        rendered = <NavLink exact to={action.path} className={classNames('nav-link', cls)}>
          {icon}
          {title}
        </NavLink>
      }
      else {
        rendered = <a href='#' className={classNames('nav-link', cls)} onClick={preventDefault}>
          {icon}
          {title}
        </a>
      }
    }
    else {
      console.error('nav action item require key with either navComponent or path+icon+title')
      return null
    }
    return (
      <NavErrorBoundary key={`${action.key}-${i}`} context={`action:${action.key} ${action.path || ''}`.trim()}>
        <li data-nav-key={action.key} className='nav-item'>
          {rendered}
        </li>
      </NavErrorBoundary>
    )
  }).filter(Boolean) || null, [filteredActions, collapsedCls, isSideNavCollapsed, stateToProps])
  return (
    <div className={`${hideNavRoutes ? 'spf-h-full' : 'spf-flex-grow-0'} spf-w-full nav-actions`}>
      <ul className='nav flex-column'>
        {navItems}
      </ul>
    </div>
  )
}

export default memo(NavActions)
